import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader, ActionsCell } from "@/components/data-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { format } from "date-fns";
import type { Company } from "@/lib/api/admin-api";

type NavigateFn = (companyId: number | string) => void;
type EditFn = (company: Company) => void;

function getAddressLabel(company: Company): string {
  const addr = company.locations?.[0];
  if (!addr) return "—";
  const parts = [addr.addressLine1, addr.city, addr.state, addr.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
}

export function getCompanyColumns(
  i18n: I18n,
  navigate: NavigateFn,
  onEdit: EditFn,
  getOrganizationName?: (company: Company) => string,
): ColumnDef<Company, unknown>[] {
  return [
    {
      accessorKey: "id",
      meta: { label: i18n._(msg`ID`) },
      header: i18n._(msg`ID`),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-(--color-text-secondary)">
          {getValue() as number}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue() as string}</span>
      ),
    },
    {
      id: "organization",
      meta: { label: i18n._(msg`Organization`) },
      header: i18n._(msg`Organization`),
      cell: ({ row }) => (
        <span>{getOrganizationName?.(row.original) ?? row.original.organization?.name ?? "—"}</span>
      ),
      enableSorting: false,
    },
    {
      id: "address",
      meta: { label: i18n._(msg`Address`), defaultVisible: false },
      header: i18n._(msg`Address`),
      cell: ({ row }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {getAddressLabel(row.original)}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "isActive",
      meta: { label: i18n._(msg`Status`) },
      header: i18n._(msg`Status`),
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? "success" : "secondary"}>
          {getValue() ? i18n._(msg`Active`) : i18n._(msg`Inactive`)}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "createdAt",
      meta: { label: i18n._(msg`Created`), defaultVisible: false },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Created`)} />
      ),
      cell: ({ getValue }) =>
        format(new Date(getValue() as string), "MMM d, yyyy"),
    },
    {
      accessorKey: "updatedAt",
      meta: { label: i18n._(msg`Updated`), defaultVisible: false },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Updated`)} />
      ),
      cell: ({ getValue }) =>
        format(new Date(getValue() as string), "MMM d, yyyy"),
    },
    {
      id: "users",
      meta: { label: i18n._(msg`Users`), defaultVisible: false },
      header: i18n._(msg`Users`),
      cell: ({ row }) => row.original._count?.users ?? 0,
      enableSorting: false,
    },
    {
      id: "actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <ActionsCell
          items={[
            {
              label: i18n._(msg`View`),
              onClick: () => navigate(row.original.id),
            },
            {
              label: i18n._(msg`Edit`),
              onClick: () => onEdit(row.original),
            },
            {
              label: i18n._(msg`Delete`),
              onClick: () => {},
              destructive: true,
            },
          ]}
        />
      ),
    },
  ];
}
